import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookMarked, BookOpen, Highlighter } from "lucide-react";
import api from "@/lib/api";
import BibleReader, { useBibleVersions } from "@/components/BibleReader";
import CommentaryPanel, { COMMENTARY_KEY, useCommentaries } from "@/components/commentary/CommentaryPanel";
import { useHighlights } from "@/components/highlights/useHighlights";
import { useAuth } from "@/context/AuthContext";
import { BackToTop } from "@/components/ChapterNav";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import Seo from "@/components/Seo";

const LAST_KEY = "qg-reader-last";
const PANEL_KEY = "qg-reader-commentary";

const Count = ({ n }) => (n ? <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-gold/15 px-1.5 font-mono text-[9px] text-gold" title={`${n} highlight${n === 1 ? "" : "s"}`}><Highlighter className="h-2.5 w-2.5" />{n}</span> : null);

export default function Read() {
  const { book, chapter } = useParams();
  const [sp, setSp] = useSearchParams();
  const verse = Number(sp.get("v")) || null;
  const flashId = sp.get("hl");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: books } = useBibleVersions();
  const { data: counts } = useQuery({ queryKey: ["highlight-counts"], queryFn: async () => (await api.get("/highlights/counts")).data.counts, enabled: !!user, staleTime: 60 * 1000 });
  const [panel, setPanel] = useState(() => localStorage.getItem(PANEL_KEY) === "1");
  const [slug, setSlug] = useState(() => localStorage.getItem(COMMENTARY_KEY) || "matthew-henry");
  const [mobileTab, setMobileTab] = useState("bible");
  const { data: commentaries } = useCommentaries();
  const hasCommentary = !!commentaries?.length;
  useEffect(() => { localStorage.setItem(PANEL_KEY, panel ? "1" : "0"); }, [panel]);
  useEffect(() => { localStorage.setItem(COMMENTARY_KEY, slug); }, [slug]);
  const wantSlug = sp.get("commentary");
  useEffect(() => {
    if (!wantSlug) return;
    setSlug(wantSlug);
    setPanel(true);
    const next = new URLSearchParams(sp);
    next.delete("commentary");
    setSp(next, { replace: true });
  }, [wantSlug]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: react only to a new ?commentary= link
  const hl = useHighlights(book, chapter);

  useEffect(() => {
    if (!book) {
      const last = localStorage.getItem(LAST_KEY) || "Matthew/1";
      navigate(`/read/${last}`, { replace: true });
    } else {
      localStorage.setItem(LAST_KEY, `${book}/${chapter || 1}`);
    }
  }, [book, chapter, navigate]);

  if (!book) return null;
  const ch = Number(chapter) || 1;
  const total = books?.books.find((b) => b.name === book)?.chapters || 1;
  const go = (b, c) => { navigate(`/read/${encodeURIComponent(b)}/${c}`); window.scrollTo({ top: 0 }); };
  const setVerse = (v) => { const next = new URLSearchParams(sp); next.set("v", String(v)); next.delete("hl"); setSp(next, { replace: true }); };
  const bookCount = (b) => Object.values(counts?.[b] || {}).reduce((a, n) => a + n, 0);

  const list = books?.books || [];
  const ot = list.filter((b) => b.testament === "OT");
  const nt = list.filter((b) => b.testament === "NT");
  const testament = list.find((b) => b.name === book)?.testament;
  const picker = (
    <div className="flex min-w-0 items-center gap-1.5 sm:gap-2" data-testid="reader-picker">
      <div className="flex shrink-0 overflow-hidden rounded-full border border-border bg-background font-mono text-[10px] uppercase tracking-wider" data-testid="reader-testament-toggle">
        <button type="button" onClick={() => go("Genesis", 1)} data-testid="reader-testament-ot" aria-pressed={testament === "OT"}
          className={`px-2 py-1.5 transition-colors ${testament === "OT" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>OT</button>
        <button type="button" onClick={() => go("Matthew", 1)} data-testid="reader-testament-nt" aria-pressed={testament === "NT"}
          className={`px-2 py-1.5 transition-colors ${testament === "NT" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>NT</button>
      </div>
      <Select value={book} onValueChange={(b) => go(b, 1)}>
        <SelectTrigger className="h-8 w-[128px] min-w-0 rounded-full bg-background font-serif text-[15px] sm:w-[170px] sm:text-base" data-testid="reader-book-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-[60vh]">
          <SelectGroup>
            <SelectLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Old Testament</SelectLabel>
            {ot.map((b) => <SelectItem key={b.name} value={b.name}>{b.name}<Count n={bookCount(b.name)} /></SelectItem>)}
          </SelectGroup>
          <SelectGroup>
            <SelectLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">New Testament</SelectLabel>
            {nt.map((b) => <SelectItem key={b.name} value={b.name}>{b.name}<Count n={bookCount(b.name)} /></SelectItem>)}
          </SelectGroup>
        </SelectContent>
      </Select>
      <Select value={String(ch)} onValueChange={(c) => go(book, Number(c))}>
        <SelectTrigger className="h-8 w-[72px] shrink-0 rounded-full bg-background font-mono text-sm sm:w-[84px]" data-testid="reader-chapter-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-[60vh]">
          {Array.from({ length: total }, (_, i) => i + 1).map((c) => <SelectItem key={c} value={String(c)}>{c}<Count n={counts?.[book]?.[String(c)]} /></SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const colCls = "h-[calc(100vh-12rem)] lg:h-[calc(100vh-7.5rem)] lg:sticky lg:top-20";
  return (
    <div className={`mx-auto px-4 py-8 sm:px-6 lg:px-8 ${panel ? "max-w-[1500px]" : "max-w-3xl"}`} data-testid="read-page" data-commentary-open={panel ? "1" : "0"}>
      <Seo title={`${book} ${ch} - Bible Reader`} description={`Read ${book} chapter ${ch} with the questions asked in it marked verse by verse.`} />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Bible Reader</span>
          <h1 className="mt-1 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl" data-testid="read-title">{book} {ch}</h1>
        </div>
        {hasCommentary && <button type="button" onClick={() => setPanel((p) => !p)} aria-pressed={panel} data-testid="reader-commentary-toggle"
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${panel ? "border-gold bg-gold/15 text-foreground" : "border-border text-muted-foreground hover:border-gold hover:text-foreground"}`}>
          <BookMarked className="h-4 w-4 text-gold" /> {panel ? "Hide commentary" : "Show commentary"}
        </button>}
      </div>
      {panel && (
        <div className="mb-3 flex overflow-hidden rounded-full border border-border bg-background font-mono text-[11px] uppercase tracking-wider lg:hidden" data-testid="reader-mobile-tabs">
          {[["bible", "Bible", BookOpen], ["commentary", "Commentary", BookMarked]].map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setMobileTab(k)} aria-pressed={mobileTab === k} data-testid={`reader-mobile-tab-${k}`}
              className={`flex flex-1 items-center justify-center gap-1.5 py-2 transition-colors ${mobileTab === k ? "bg-gold text-white" : "text-muted-foreground"}`}><Icon className="h-3.5 w-3.5" /> {l}</button>
          ))}
        </div>
      )}
      <div className={panel ? "lg:grid lg:grid-cols-2 lg:items-start lg:gap-6" : ""}>
        <div className={panel ? `${colCls} ${mobileTab === "bible" ? "" : "hidden lg:block"}` : ""}>
          <BibleReader book={book} chapter={ch} verse={verse} verseChapter={verse ? ch : null} activeId={null} onChapter={(c) => go(book, c)} standalone picker={picker} scroll={panel} onVerseClick={panel ? setVerse : null} flashId={flashId} />
        </div>
        {panel && (
          <div className={`${colCls} mt-4 lg:mt-0 ${mobileTab === "commentary" ? "" : "hidden lg:block"}`}>
            <CommentaryPanel slug={slug} onSlug={setSlug} book={book} chapter={ch} verse={verse} onJump={(v) => { setVerse(v); setMobileTab("bible"); }} actions={hl} highlights={hl.all.filter((h) => h.source === "commentary" && h.commentary === slug)} />
          </div>
        )}
      </div>
      <BackToTop />
    </div>
  );
}
